import moment, { Moment } from 'moment-timezone'
import { isDaycareOpenOnMoment, isDaycareOpenThisDay } from '@/modules/helper/daycareHelper'
import { Daycare } from '@/types/Daycare'
import { DAY_FORMAT, HOUR_MINUTE_FORMAT, INVALID_DATES_ERROR } from './constants'
import { DateSelection } from './dateSelection'
import { momentDay } from './common'

export interface OccasionalSlotsValidationProps {
    dates: DateSelection
    start: string
    end: string
    sectionId: number | undefined
    count: number
    confirmedChildIds: number[]
}

export interface OccasionalSlotsValidationErrors {
    date: boolean
    time: boolean
    count: boolean
    children: boolean
    section: boolean
}

export function resolveDatesForDaycare(daycare: Daycare, dates: DateSelection): string[] {
    switch (dates.mode) {
        case 'single':
            return [dates.date]
        case 'list':
            return dates.dates.filter((day: string): boolean => isDaycareOpenThisDay(daycare, { day }))
        case 'range': {
            const start: Moment = momentDay(dates.startDate)
            const end: Moment = momentDay(dates.endDate)
            if (start.isAfter(end)) throw new Error(INVALID_DATES_ERROR)
            const days: string[] = []
            for (const day: Moment = start.clone(); day.isSameOrBefore(end, 'day'); day.add(1, 'day')) {
                if (isDaycareOpenThisDay(daycare, { moment: day })) days.push(day.format(DAY_FORMAT))
            }
            return days
        }
    }
}

export function areSelectedDatesWellFormed(dates: DateSelection): boolean {
    const selected: string[] =
        dates.mode === 'single' ? [dates.date] : dates.mode === 'range' ? [dates.startDate, dates.endDate] : dates.dates
    return selected.length > 0 && selected.every((date: string): boolean => moment(date, DAY_FORMAT, true).isValid())
}

function getValidDates(daycare: Daycare, dates: DateSelection): string[] {
    if (!areSelectedDatesWellFormed(dates)) return []
    try {
        return resolveDatesForDaycare(daycare, dates).filter((day: string): boolean =>
            isDaycareOpenThisDay(daycare, { day })
        )
    } catch {
        return []
    }
}

export function isDateSelectionValid(daycare: Daycare, dates: DateSelection): boolean {
    return getValidDates(daycare, dates).length > 0
}

export function getOccasionalSlotsErrors(
    daycare: Daycare,
    { dates, start, end, sectionId, count, confirmedChildIds }: OccasionalSlotsValidationProps
): OccasionalSlotsValidationErrors {
    const days: string[] = getValidDates(daycare, dates)
    const startMoment: Moment = moment(start, HOUR_MINUTE_FORMAT, true)
    const endMoment: Moment = moment(end, HOUR_MINUTE_FORMAT, true)
    return {
        date: days.length === 0,
        time:
            !startMoment.isValid() ||
            !endMoment.isValid() ||
            startMoment.isAfter(endMoment) ||
            days.some(
                (day: string): boolean =>
                    !isDaycareOpenOnMoment(daycare, { day, time: start }) ||
                    !isDaycareOpenOnMoment(daycare, { day, time: end })
            ),
        count: !Number.isInteger(count) || count < 1,
        children: confirmedChildIds.length > count,
        section: !sectionId
    }
}
